(ns nosco.welcome
  (:require
   ["react" :as React]
   ["react-dom/client" :as react-dom]
   [clojure.edn :as edn]
   [clojure.string :as string]
   [goog.object :as gobj]
   [hx.hooks :refer [useState] :as hooks]
   [hx.react :as hx :refer [defnc]]
   [medley.core :as medley]
   [nosco.i18n.translate :as t]
   [nosco.icons]
   [nosco.icons2 :as icons2]
   nosco.lazy
   [nosco.router :as router]
   [nosco.ui.base]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.errors :as errors]
   [nosco.ui.forms]
   [nosco.ui.html :as html]
   [nosco.ui.inputs :as inputs]
   [nosco.ui.loading :as loading]
   [nosco.ui.messages :as messages]
   [nosco.ui.welcome.welcome-components :as wc]
   [nosco.util :as util]
   [nosco.welcome.network :as wn]
   [reseda.react]
   [reseda.state]
   [nosco.ui.passwords :as ui-passwords]
   [ajax.core :as ajax])
  (:import
   goog.net.Cookies))

(defn reset-password! [token password activate]
  (js/Promise.
   (fn [resolve reject]
     (ajax/PUT
      (router/href ::reset-password nil (cond-> {:token token} activate (assoc :activate true)))
      {:format (ajax/url-request-format)
       :headers {"X-Nosco-Anti-Forgery" "1"}
       :params {:password password}
       :response-format (ajax/json-response-format {:keywords? true})

       :handler
       (fn []
         (resolve))

       :error-handler
       (fn [error]
         (if (= 422 (:status error))
           (reject (:response error))
           (throw (clj->js error))))}))))

(defnc ResetPasswordForm [{:keys [token activate]}]
  (let [[password ->password] (hooks/useState "")
        [confirm-password ->confirm-password] (hooks/useState "")
        [saving ->saving] (hooks/useState false)
        [error ->error] (hooks/useState nil)
        submit-disabled (or saving (string/blank? password) (string/blank? confirm-password))]
    [:form
     {:on-submit
      (fn [event]
        (.preventDefault event)

        (cond
          submit-disabled
          nil

          (not= password confirm-password)
          (->error ::ui-passwords/not-matching)

          :else
          (do
            (->saving true)
            (js/setTimeout
             #(-> (reset-password! token password activate)
                  (.then
                   (fn []
                     (set! js/location "/?reset-password=true")))
                  (.catch
                   (fn [response]
                     (prn response)
                     (->saving false)
                     (->error (:error response)))))
             0))))}

     [ui-passwords/PasswordWithConfirmationFields
      {:password-label (t/t "Password")
       :confirm-password-label (t/t "Confirm password")
       :password-value password
       :confirm-password-value confirm-password

       :error error

       :on-password-change
       (fn [^js event]
         (->password (.. event -target -value))
         (->error nil))

       :on-confirm-password-change
       (fn [^js event]
         (->confirm-password (.. event -target -value))
         (->error nil))}]

     [:div {:class "mt-spacing-24"}
      [buttons2/Button
       {:variant :primary-black
        :label (if activate
                 (t/t "Activate account")
                 (t/t "Reset password and login"))
        :type "submit"
        :loading saving
        :disabled submit-disabled
        :class "w-100"}]]]))

(defonce ^:export init-data
  (some->
   (gobj/get js/window "init_data_raw")
   (edn/read-string)))

(defonce state
  (atom
   {:state (:phase init-data)}))

(defn- handle-router-navigate [new-match]
  (reset! router/current-route-match
          new-match))

(def welcome-routes
  [;; nosco-app doesn't have a login-forgot-password URL, but since we're doing client-side routing,
   ;; we can add it for convenience. /login* routes will get sent to the nosco-app login handler,
   ;; so it should also work as a bookmark (TODO confirm login* logic is still there)
   ["/login/forgot-password" {:name ::forgot-password}]
   ["/login" {:name ::login}]
   ["/logout" {:name ::logout}]
   ["/welcome" {:name ::welcome}]
   ["/account" {:name ::account}]
   ["/auth/reset-password" {:name ::reset-password}]])

(defonce store (reseda.state/new-store state))
(defn subscribe [selector]
  (reseda.react/useStore store selector))

(defonce current-route-store (reseda.state/new-store router/current-route-match))
(defn subscribe-current-modal [route]
  (some-> route
          :query-params
          :modal
          keyword))

(defn hide-modal! []
  (router/push-state (router/current-route-name)
                     (router/current-route-path-params)
                     (dissoc (router/current-route-query-params) :modal)))

(defn signup-form-submit [form callback errback]
  (let [params (nosco.ui.forms/form-data form)]
    (wn/POST (.-action form)
      params
      callback
      errback)))

(defn login-form-errors [form-data]
  (cond-> {}
    (nosco.ui.forms/form-data-value-empty? form-data "email")
    (assoc "email" (inputs/input-error (t/t "Email required")))
    (nosco.ui.forms/form-data-value-empty? form-data "password")
    (assoc "password" (inputs/input-error (t/t "Password required")))))

(defn login-form-submit [form callback errback]
  (let [params (nosco.ui.forms/form-data form)
        errors (login-form-errors params)]
    (if (empty? errors)
      (wn/nosco-app-POST (.-action form)
                         params
                         callback
                         errback)
      (errback {:form-errors errors}))))

(defn reset-form-errors [form-data]
  (cond-> {}
    (nosco.ui.forms/form-data-value-empty? form-data "email")
    (assoc "email" (inputs/input-error (t/t "Email required")))))

(defn reset-password-submit [form callback errback]
  (let [params (nosco.ui.forms/form-data form)
        errors (reset-form-errors params)]
    (if (empty? errors)
      (wn/POST (.-action form)
        params
        callback
        errback)
      (errback {:form-errors errors}))))

(defn error-translation
  "Translate an error key into a friendly prompt."
  [k]
  (when k
    (get
     {"required" (t/t "This field is required.")
      "allowed-domains" (t/t "You need an approved domain to register.")
      "invalid-email" (t/t "Invalid email")
      "validation-error" (t/t "Please fix the errors above.")}
     k (t/t "Something went wrong."))))

(defn welcome-errors->form-errors [error]
  (let [form-errors (get error "form-errors")]
    (medley/map-vals
     (fn [error-keys]
       (let [error-texts (keep error-translation error-keys)]
         (when error-texts
           (inputs/input-error (string/join "\n" error-texts)))))
     form-errors)))

(comment
  (welcome-errors->form-errors
   {"form-errors"
    {"How did you hear about this event?" ["required"]
     "Last Name" ["required"]}}))

(defnc WelcomeForm
  "The main form with recaptcha integration."
  ;; recaptcha is annoying in that it needs to be given a callback
  ;; to run (of course). So when the form is submitted, we execute
  ;; the recaptcha which will do stuff and only on success will it
  ;; then call the callback which will submit the form.
  []
  (let [[in-flight set-in-flight!] (useState false)
        [error set-error!] (useState nil)
        form-id "main-form"
        on-success (fn [_response]
                     (swap! state assoc :state :success))
        on-error (fn [response]
                   (set-in-flight! false)
                   (js/console.log "response" response)
                   (set-error! (:response response)))
        on-submit (fn [e]
                    (.preventDefault e)
                    (set-in-flight! true)
                    (set-error! nil)
                    (.execute js/grecaptcha))]
    ;; TODO main-help should probably be merged into the intro
    #_(when main-help
        [:div {:class "font-size-sm text-muted"} main-help])
    [nosco.ui.forms/Form
     {:form-desc (:form-desc init-data)
      :form-id form-id
      :on-submit on-submit
      :form-values nil
      :form-errors (welcome-errors->form-errors error)}
     [wc/Recaptcha {:site-key (:recaptcha-sitekey init-data)
                    :callback-fn (fn [_token]
                                     ;; recaptcha generates a hidden textarea so we get
                                     ;; the token from there -- no need to do anything with
                                     ;; the arg
                                   (js/console.log "received recaptcha token")
                                   (signup-form-submit (.getElementById js/document form-id)
                                                       on-success
                                                       on-error)
                                   ;; It seems that recaptcha has to be reset syncronously,
                                   ;; upon receiving the token, otherwise an error is thrown
                                   ;; while resetting the recaptcha object
                                   (.reset js/grecaptcha))}]
     (when-let [message (get error "message")]
       [wc/ErrorMessage {:text (error-translation message)}])
     [:div {:class "mt-spacing-32"}]
     [buttons2/Button
      {:type "submit"
       :variant :primary-black
       :label (t/t "Create account")
       :loading in-flight
       :disabled in-flight
       :class "w-100"}]
     [wc/TermsContainer
      [wc/TermsConditionsText init-data]
      [wc/RecaptchaText]]]))

(defnc Disabled []
  [:div {:class "welcome-center-area"}
   [wc/WarningMessage
    {:title (t/t "You cannot currently create an account")
     :text
     (t/t-html "If you already have an account, please proceed to the <a href='%s' %s>login page</a>"
               ;; ensure the page fully reloads, so that we hit nosco-app and redirect to SSO
               "/login" "data-reititignoreclick=true")}]])

(defnc WelcomeArea [{:keys [signup-enabled? social-providers email-button-text login-method-buttons-order]}]
  (case (subscribe :state)
    :signup
    [wc/Signup {:signup-enabled? signup-enabled?
                :social-providers social-providers
                :email-button-text email-button-text
                :login-method-buttons-order login-method-buttons-order
                :signup-form (hx/f [WelcomeForm {:form (:form-desc init-data)}])}]

    :disabled [Disabled]

    :success [wc/SuccessMessage
              {:text
               [html/SafeHTMLStrict {:html (:confirmation (:text init-data))}]}]))

(defn login-form []
  ;; these should be :required but we don't want the red asterisk to show up
  ;; so we validate manually before form submission
  [{:title (t/t "Email")
    :type :email
    :name "email"
    :auto-complete "username"}

   {:title (t/t "Password")
    :type :password
    :name "password"
    :auto-complete "current-password"
    :message
    (hx/f
     [:a {:href "/login/forgot-password"}
      (t/t "Forgot password?")])}])

(defn password-reset-form []
  ;; these should be :required but we don't want the red asterisk to show up
  ;; so we validate manually before form submission
  [{:title "" :type :email :name "email" :placeholder (t/t "Email")}])

(defn login-error-code->msg
  "Convert a nosco-app login error code to a human error. See nosco-app login.html for the source
   of this logic."
  [error-code]
  (when error-code
    (case error-code
      "1" {:title (t/t "Password or email incorrect")
           :text (t/t-html "It looks like you may have forgotten your password.<br><a href='%s'>Click here to reset it</a>."
                           "/login/forgot-password")}
      "inactive"  {:title (t/t "Account inactive")
                   :text (t/t "This account has been set to inactive. Please contact a site administrator if you believe this to be an error.")}
      "blocked"  {:title (t/t "Account blocked")
                  :text (t/t "This account has been blocked due to too many failed login attempts. Please contact a site administrator to have the account unblocked.")}
      "deleted" {:title (t/t "Account deleted")
                 :text (t/t "This account has been deleted. Please contact a site administrator if you believe this to be an error.")}
      "passwordless" {:title (t/t "Account needs activation")
                      :text (t/t-html "This account does not have a password yet. <br><a href='%s'>Click here to create a password</a>."
                                      "/login/forgot-password")}
      ("2" "3" "access_denied") {:title (t/t "Access denied")}
      {:title (t/t "Unexpected error")
       :text (t/t "Please try again. If the error persists, please contact support.")})))

(defn add-ref-to-location! [redir-ref]
  (router/update-query-params-silently! "ref" redir-ref))


(defnc ForgotPasswordArea []
  (let [form-id "reset-password-form"
        [in-flight set-in-flight!] (useState false)
        [error-message set-error-message!] (useState nil)
        [form-errors set-form-errors] (useState nil)
        [success set-success!] (useState false)
        on-success (fn []
                     (set-in-flight! false)
                     (set-success! true))
        on-error (fn [{:keys [status form-errors]}]
                   (set-form-errors form-errors)
                   (when (some? status)
                     (set-error-message! "There was an error processing your request."))
                   (set-in-flight! false))]
    [wc/Form {:title (t/t "Forgot your password?")
              :help (t/t "Enter your email address to reset your password. You may need to check your Junk E-mail folder.")}
     [nosco.ui.forms/Form
      {:form-desc (password-reset-form)
       :on-submit (fn [e]
                    (.preventDefault e)
                    (set-error-message! nil)
                    (set-form-errors nil)
                    (set-success! nil)
                    (set-in-flight! true)
                    (.execute js/grecaptcha))
       :action "/login/forgot-password"
       :method "POST"
       :form-id form-id
       :form-values nil
       :form-errors form-errors}
      [wc/Recaptcha {:site-key (:recaptcha-sitekey init-data)
                     :callback-fn (fn [_token]
                                    (reset-password-submit (.getElementById js/document form-id)
                                                           on-success
                                                           on-error)
                                    ;; It seems that recaptcha has to be reset syncronously,
                                    ;; upon receiving the token, otherwise an error is thrown
                                    ;; while resetting the recaptcha object
                                    (.reset js/grecaptcha))}]
      (when error-message
        [wc/ErrorMessage {:text error-message}])
      [:div {:class "mt-spacing-32"}]
      [buttons2/Button {:variant :primary-black
                        :type "submit"
                        :disabled in-flight
                        :label (t/t "Send reset link")
                        :class "w-100"}]
      (when success
        [:<>
         [:div {:class "mt-spacing-32"}]
         [wc/SuccessMessage {:text (t/t "An email with a reset link has been sent.
                           Click on the link to change the password. You may
                           need to check your Junk E-mail folder.")}]])
      [wc/TermsContainer
       [wc/RecaptchaText]]]]))

(defonce login-msg
  (let [c ^js (.getInstance Cookies)
        msg (.get c "nosco-login-msg")]
    (.remove c "nosco-login-msg")
    msg))

(defn use-get-login-msg []
  login-msg)

(defnc LoginMsg []
  (let [login-msg (use-get-login-msg)]
    (when login-msg
      [:div {:class "mb-spacing-40"}
       (case login-msg
         "timeout"
         [wc/WarningMessage {:text (t/t "Your session has timed out. Please login again.")}]
         nil)])))

(defnc LoginForm []
  (let [form-id "login-form"
        [in-flight set-in-flight!] (useState false)
        [error-message set-error-message!] (useState nil)
        [form-errors set-form-errors] (useState nil)
        on-success (fn [successURL]
                     (set! (.-location js/window) successURL))
        on-error (fn [{:keys [error-code redir-ref form-errors]}]
                   (set-in-flight! false)
                   (when redir-ref
                     (add-ref-to-location! redir-ref))
                   (set-form-errors form-errors)
                   (set-error-message! (login-error-code->msg error-code)))]
    [nosco.ui.forms/Form
     {:form-desc (login-form)
      :on-submit (fn [e]
                   (.preventDefault e)
                   (set-in-flight! true)
                   (set-error-message! nil)
                   (login-form-submit (.getElementById js/document form-id)
                                      on-success
                                      on-error))
      :action "/authorize"
      :method "POST"
      :form-id form-id
      :form-values nil
      :form-errors form-errors}
     (when error-message
       [wc/ErrorMessage {:title (:title error-message)
                         :text (:text error-message)}])
     [:div {:class "mt-spacing-32"}]
     [buttons2/Button {:variant :primary-black
                       :type "submit"
                       :disabled in-flight
                       :loading in-flight
                       :label (t/t "Login")
                       :class "w-100"}]]))

(defnc Header []
  [wc/Header {:login-logo (:login-logo init-data)
              :login-link (or (:login-link init-data) "/")
              :title (:title (:text init-data))
              :intro (hx/f [html/SafeHTMLStrict {:html (:intro (:text init-data))}])}])

(defnc Footer []
  [wc/Footer
   {:powered-by-text (t/t "powered by")}])

(defnc LogoutArea []
  [:div {:class "welcome-center-area"}
   [messages/SectionMessage
    {:title (t/t "You have been logged out")
     :variant :info}
    (t/t-html "To log back in, please go to the <a href='%s' %s>login page</a>"
               ;; ensure the page fully reloads, so that we hit nosco-app and redirect to SSO
              "/login" "data-reititignoreclick=true")]])

(defnc MainColumn []
  (let [curr-route (reseda.react/useStore current-route-store identity)
        route-name (get-in curr-route [:data :name])
        route-params (get curr-route :parameters)
        sso-enabled? (:sso-enabled? init-data)
        signup-enabled? (= :signup (:phase init-data))
        social-providers (:social-signup-providers init-data)
        sso-button-text (get-in init-data [:text :sso-button])
        email-button-text (-> (get-in init-data [:text :email-button])
                              (update :title (fnil identity (t/t "Continue with email & password"))))
        login-method-buttons-order (:login-method-buttons-order init-data)
        redirect-to (-> route-params :query :redirect-to)
        sso-href (:sso-login-href init-data)
        ;; TODO cleanup redirect-to here for cleanliness
        ;; TODO cleanup redirect-to when acting on it, to avoid infinite loops
        sso-provider {:href (str sso-href (when redirect-to (str "?redirect-to=" (js/encodeURIComponent redirect-to))))
                      :tooltip (:tooltip sso-button-text)
                      :label (or (:title sso-button-text)
                                 (t/t "Continue with SSO"))}]
    [wc/WelcomeContainer
     [Header]
     (case route-name
       ::welcome
       [WelcomeArea
        {:signup-enabled? signup-enabled?
         :social-providers social-providers
         :email-button-text email-button-text
         :login-method-buttons-order login-method-buttons-order}]
       ::forgot-password
       [ForgotPasswordArea]
       ::logout
       [LogoutArea]
       ::account
       [wc/ResetPassword {:activate (-> route-params :query :activate)}
        [ResetPasswordForm
         {:token (or (-> route-params :query :activate)
                     (-> route-params :query :reset))
          :activate (-> route-params :query :activate)}]]
       ;; all other matches, including ::login, go to the login form
       ;; avoiding a crash when people mistype something
       [:<>
        [LoginMsg]
        [wc/LoginArea {:sso-enabled? sso-enabled?
                       :signup-enabled? signup-enabled?
                       :social-providers social-providers
                       :email-button-text email-button-text
                       :login-method-buttons-order login-method-buttons-order
                       :login-form (hx/f [LoginForm])
                       :sso-provider (when sso-enabled? sso-provider)}]])
     [Footer]]))

(defn onRecaptchaLoad [_arg]
  (swap! state assoc :recaptcha-loaded true))

;; called by recaptcha when loaded, needed to avoid race conditions
(gobj/set js/window "noscoOnRecaptchaLoad" onRecaptchaLoad)

(defn- mail-to-with-subject [mailto-link]
  (if (and (string/starts-with? mailto-link "mailto:")
           (not (string/includes? mailto-link "?")))
    (str mailto-link
         "?subject="
         (t/t "Problems logging in at %s" js/window.location.hostname))
    mailto-link))

(defnc Main []
  ;; I've left the scaffolding here but we don't really
  ;; do error handling at this level in this page. Need
  ;; to probably have an error context and re-think the
  ;; whole error handling approach for frontend.
  (let [error nil
        current-modal (reseda.react/useStore current-route-store subscribe-current-modal)]
    [React/StrictMode
     [nosco.ui.base/ModalManager
      [React/Suspense {:fallback (hx/f [loading/LoadingNew])}
       [nosco.icons/MainSVG]
       (if error
         [errors/ErrorMessage {:error error}]
         [errors/ErrorBoundary
          (if (and (:recaptcha-sitekey init-data)
                   (not (subscribe :recaptcha-loaded)))
            ;; wait for recaptcha to be loaded before showing any forms that might need it
            [loading/LoadingNew]
            [:div {:class "welcome-layout"}
             [wc/TextModal {:showing (= :terms-conditions current-modal)
                            :close hide-modal!
                            :title (or (:terms-conditions-link-title init-data)
                                       (t/t "Terms & Conditions"))
                            :html (:terms-conditions init-data)}]
             [wc/TextModal {:showing (= :privacy-policy current-modal)
                            :close hide-modal!
                            :title (or
                                    (:privacy-policy-link-title init-data)
                                    (t/t "Privacy Policy"))
                            :html (:privacy-policy-text init-data)}]
             [MainColumn]
             [wc/MainImage {:image-url (:image-url init-data)
                            :background-size (if (string/blank? (:background-size init-data))
                                               "cover"
                                               (:background-size init-data))
                            :site-color (:site-color init-data)}]
             (let [contact-us-link (or (:contact-us-link init-data)
                                       "https://help.nos.co/hc/en-us/requests/new")
                   contact-us-link (util/email->mailto contact-us-link)
                   contact-us-link (mail-to-with-subject contact-us-link)
                   terms-conditions-link-title (or (:terms-conditions-link-title init-data)
                                                   (t/t "Terms & Conditions"))
                   privacy-policy-link-title (or
                                              (:privacy-policy-link-title init-data)
                                              (t/t "Privacy Policy"))
                   terms-conditions-display (= "show" (:terms-conditions-display init-data))
                   privacy-policy-display (= "show" (:privacy-policy-display init-data))]
               [wc/HelpMenu {:sections
                             [{:title (t/t "Top help articles")
                               :items [{:label (t/t "Can't login")
                                        :href "https://help.nos.co/hc/en-us/articles/115002516371-You-can-t-login-What-to-do-"
                                        :target "_blank"
                                        :icon icons2/Semantic-Help-small}
                                       {:label (t/t "Reset your password")
                                        :href "https://help.nos.co/hc/en-us/articles/208644016-Reset-your-password"
                                        :target "_blank"
                                        :icon icons2/Semantic-Help-small}
                                       {:label (t/t "Activate account")
                                        :href "https://help.nos.co/hc/en-us/articles/360014018159-Activate-your-account"
                                        :target "_blank"
                                        :icon icons2/Semantic-Help-small}
                                       {:label (t/t "Contact us")
                                        :href contact-us-link
                                        :target "_blank"
                                        :icon icons2/Send-small}]}
                              {:items [(when (and terms-conditions-display
                                                  (not (string/blank? (:terms-conditions init-data))))
                                         {:label terms-conditions-link-title
                                          :href "?modal=terms-conditions"})
                                       (when privacy-policy-display
                                         {:label privacy-policy-link-title
                                          :href "?modal=privacy-policy"})]}]}])])])]]]))

(defonce ROOT (atom nil))

(defn ^:dev/after-load -render []
  (when @ROOT
    (.render ^js @ROOT (hx/f [Main]))))

(defn ^:export start [el]
  (reset! ROOT (react-dom/createRoot el))
  (router/start! welcome-routes handle-router-navigate)

  (-render))

(defn- ^:dev/after-load reload-routes! []
  (router/reload! welcome-routes))
